import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  BuildingOffice2Icon,
  PhoneIcon,
  Squares2X2Icon,
  TagIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { status } from "./statusMap";
import { PMSelectMenu } from "./PMFilterPopover";

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

function TWcn(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Pill button: one per filter-field (Status, Customer …)
function FilterPill({ label, icon: Icon, count, active, onClick, disabled, fullWidth = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={TWcn(
        "inline-flex h-[46px] items-center gap-[9px] rounded-full border px-[18px] text-[14.5px] font-semibold transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
        fullWidth && "w-full justify-between",
        active
          ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
          : "border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50"
      )}
    >
      {Icon && (
        <Icon
          className={TWcn(
            "h-[16px] w-[16px] flex-shrink-0",
            active ? "text-white/70" : "text-slate-400"
          )}
        />
      )}
      {label}
      {count > 0 && (
        <span
          className={TWcn(
            "min-w-[22px] rounded-full px-[7px] py-px text-center text-[12.5px] tabular-nums",
            active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// Toggle pill (boolean checkbox-style)
function TogglePill({ label, icon: Icon, active, onClick, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={TWcn(
        "inline-flex h-[46px] items-center gap-[10px] rounded-full border pl-[18px] pr-2 text-[14px] font-semibold transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
        active
          ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
          : "border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50"
      )}
    >
      {Icon && (
        <Icon
          className={TWcn(
            "h-[16px] w-[16px] flex-shrink-0",
            active ? "text-white/70" : "text-slate-400"
          )}
        />
      )}
      {label}
      <span
        className={TWcn(
          "flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors",
          active
            ? "border-transparent bg-white/20 text-white"
            : "border-slate-300 text-slate-400"
        )}
      >
        {active && (
          <svg viewBox="0 0 12 10" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-[10px] w-[10px]">
            <polyline points="1,5 4,8 11,1" />
          </svg>
        )}
      </span>
    </button>
  );
}

// Active filter chip — one per selected value
function FilterChip({ label, colorClass, style, onRemove }) {
  return (
    <span
      className={TWcn(
        "inline-flex h-[34px] items-center gap-[10px] rounded-full pl-[14px] pr-[7px] text-[13.5px] font-semibold",
        colorClass || "bg-slate-100 text-slate-700"
      )}
      style={style}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-black/10 transition-colors hover:bg-black/20"
      >
        <XMarkIcon className="h-[12px] w-[12px]" />
      </button>
    </span>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export const ModuleFilter = ({
  onFiltersChange,
  initialFilters = {},
  /** Disables filter pills/toggles during background fetch — search input stays editable. */
  disabled = false,
  acceptedStatuses = null,
  statusOptions: suppliedStatusOptions = null,
  hideStatusFilter = false,
  searchPlaceholder = "Search...",
  filterTitle: _filterTitle = "Filter",
  moduleOptions = [],
  userOptions = [],
  projectOptions: _projectOptions = [],
  customerOptions = [],
  contactOptions = [],
  departmentOptions = [],
  categoryOptions = [],
  categoryTree = [],
  brandOptions = [],
  tagOptions = [],
  tagsFilterLabel = null,
  customCheckboxButtonFilters = [],
  customDropdownFilters = [],
  customRangeFilters = [],
  actionBar = null,
  selectedItems = [],
  usersFilterLabel = null,
  segmentedControl = null,
  filterCard = false,
  /** When true, search starts as a compact icon and expands on click. */
  compactSearch = false,
  /** Vertical panel layout (search + selected filter fields only) for dialogs/drawers. */
  panelMode = false,
  /** When true, only render active filter chips (e.g. below a collapsed filter button). */
  chipsOnly = false,
  /** When true, hide the search input (e.g. when search is shown elsewhere on mobile). */
  hideSearch = false,
  /** Disabled placeholder rows shown at the bottom of panelMode (e.g. reserved filter slots). */
  panelPlaceholderSlots = [],
}) => {
  const { t } = useTranslation();

  // ── Search ────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState(initialFilters.query || "");
  const [searchExpanded, setSearchExpanded] = useState(
    Boolean(initialFilters.query) || !compactSearch
  );
  const searchInputRef = useRef(null);
  const isSearchFocusedRef = useRef(false);
  const lastSyncedQueryRef = useRef(initialFilters.query || "");

  // ── Selection state ───────────────────────────────────────────────────────
  const [selectedStatuses, setSelectedStatuses] = useState(initialFilters.statuses || []);
  const [selectedModules, setSelectedModules] = useState(initialFilters.modules || []);
  const [selectedUsers, setSelectedUsers] = useState(initialFilters.users || []);
  const [selectedProjects, setSelectedProjects] = useState(initialFilters.projects || []);
  const [selectedCustomers, setSelectedCustomers] = useState(initialFilters.customers || []);
  const [selectedContacts, setSelectedContacts] = useState(initialFilters.contacts || []);
  const [selectedDepartments, setSelectedDepartments] = useState(initialFilters.departments || []);
  const [selectedCategories, setSelectedCategories] = useState(initialFilters.categories || []);
  const [selectedBrands, setSelectedBrands] = useState(initialFilters.brands || []);
  const [selectedTags, setSelectedTags] = useState(initialFilters.tags || []);

  const [customCheckboxButtonStates, setCustomCheckboxButtonStates] = useState(() => {
    const s = {};
    customCheckboxButtonFilters.forEach((f) => {
      s[f.key] = initialFilters[f.key] ?? f.defaultValue ?? false;
    });
    return s;
  });

  const [customDropdownStates, setCustomDropdownStates] = useState(() => {
    const s = {};
    customDropdownFilters.forEach((f) => {
      const v = initialFilters[f.key];
      s[f.key] = Array.isArray(v) ? v : v ? [v] : [];
    });
    return s;
  });

  const [customRangeStates, setCustomRangeStates] = useState(() => {
    const s = {};
    customRangeFilters.forEach((f) => {
      s[`${f.key}_min`] = initialFilters[`${f.key}_min`] || "";
      s[`${f.key}_max`] = initialFilters[`${f.key}_max`] || "";
    });
    return s;
  });

  // ── Sync guards ───────────────────────────────────────────────────────────
  const isSyncingRef = useRef(false);
  const userActionRef = useRef(false);
  const lastSyncedFiltersRef = useRef(null);

  // ── Popover state ─────────────────────────────────────────────────────────
  // { field: string|null, rect: DOMRect|null }
  const [popover, setPopover] = useState({ field: null, rect: null });
  const openPopover = (field, e) => setPopover({ field, rect: e.currentTarget.getBoundingClientRect() });
  const closePopover = () => setPopover({ field: null, rect: null });

  // Popovers inside PMDialog (z-index 2000) must render above the dialog panel.
  const selectMenuZIndex = panelMode
    ? { zIndexOverlay: 2090, zIndexMenu: 2100 }
    : {};

  // ── Sync initialFilters → local state ─────────────────────────────────────
  useEffect(() => {
    if (userActionRef.current) return;

    const key = JSON.stringify({
      initialFilters,
      cdKeys: customDropdownFilters.map((f) => f.key),
      ccbKeys: customCheckboxButtonFilters.map((f) => f.key),
      crKeys: customRangeFilters.map((f) => f.key),
    });
    if (lastSyncedFiltersRef.current !== null && lastSyncedFiltersRef.current === key) return;

    isSyncingRef.current = true;

    const newQuery = initialFilters.query || "";
    if (!isSearchFocusedRef.current && newQuery !== lastSyncedQueryRef.current) {
      setSearchQuery(newQuery);
      lastSyncedQueryRef.current = newQuery;
    }

    setSelectedStatuses(initialFilters.statuses || []);
    setSelectedModules(initialFilters.modules || []);
    setSelectedUsers(initialFilters.users || []);
    setSelectedProjects(initialFilters.projects || []);
    setSelectedCustomers(initialFilters.customers || []);
    setSelectedContacts(initialFilters.contacts || []);
    setSelectedDepartments(initialFilters.departments || []);
    setSelectedCategories(initialFilters.categories || []);
    setSelectedBrands(initialFilters.brands || []);
    setSelectedTags(initialFilters.tags || []);

    const newCd = {};
    customDropdownFilters.forEach((f) => {
      const v = initialFilters[f.key];
      newCd[f.key] = Array.isArray(v) ? v : v ? [v] : [];
    });
    setCustomDropdownStates(newCd);

    const newCcb = {};
    customCheckboxButtonFilters.forEach((f) => {
      newCcb[f.key] = initialFilters[f.key] !== undefined ? initialFilters[f.key] : f.defaultValue ?? false;
    });
    setCustomCheckboxButtonStates(newCcb);

    const newCr = {};
    customRangeFilters.forEach((f) => {
      newCr[`${f.key}_min`] = initialFilters[`${f.key}_min`] || "";
      newCr[`${f.key}_max`] = initialFilters[`${f.key}_max`] || "";
    });
    setCustomRangeStates(newCr);

    lastSyncedFiltersRef.current = key;
    setTimeout(() => { isSyncingRef.current = false; }, 0);
  }, [initialFilters, customDropdownFilters, customCheckboxButtonFilters, customRangeFilters]);

  // ── applyFilters ──────────────────────────────────────────────────────────
  const applyFilters = () => {
    onFiltersChange({
      query: searchQuery,
      statuses: selectedStatuses,
      modules: selectedModules,
      users: selectedUsers,
      projects: selectedProjects,
      customers: selectedCustomers,
      contacts: selectedContacts,
      departments: selectedDepartments,
      categories: selectedCategories,
      brands: selectedBrands,
      tags: selectedTags,
      ...customCheckboxButtonStates,
      ...customDropdownStates,
      ...customRangeStates,
    });
  };

  // Fire applyFilters on search query change (debounced)
  useEffect(() => {
    if (isSyncingRef.current) return;
    const id = setTimeout(() => {
      if (!isSyncingRef.current) {
        applyFilters();
        if (userActionRef.current && !isSearchFocusedRef.current) {
          setTimeout(() => { userActionRef.current = false; }, 500);
        }
      }
    }, 300);
    return () => clearTimeout(id);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire applyFilters on any selection change
  useEffect(() => {
    if (isSyncingRef.current && !userActionRef.current) return;
    applyFilters();
    if (userActionRef.current && !isSearchFocusedRef.current) {
      setTimeout(() => { userActionRef.current = false; }, 500);
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    selectedStatuses, selectedModules, selectedUsers, selectedProjects,
    selectedCustomers, selectedContacts, selectedDepartments, selectedCategories,
    selectedBrands, selectedTags, customCheckboxButtonStates, customDropdownStates, customRangeStates,
  ]);

  // ── Direct toggle helpers ─────────────────────────────────────────────────
  const makeToggle = (setter) => (val) => {
    userActionRef.current = true;
    setter((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
  };
  const toggleStatus = makeToggle(setSelectedStatuses);
  const toggleModule = makeToggle(setSelectedModules);
  const toggleUser = makeToggle(setSelectedUsers);
  const _toggleProject = makeToggle(setSelectedProjects);
  const toggleCustomer = makeToggle(setSelectedCustomers);
  const toggleContact = makeToggle(setSelectedContacts);
  const toggleDepartment = makeToggle(setSelectedDepartments);
  const toggleCategory = makeToggle(setSelectedCategories);
  const toggleBrand = makeToggle(setSelectedBrands);
  const toggleTag = makeToggle(setSelectedTags);

  const toggleCustomDropdown = (key, val) => {
    userActionRef.current = true;
    setCustomDropdownStates((prev) => ({
      ...prev,
      [key]: prev[key].includes(val) ? prev[key].filter((v) => v !== val) : [...prev[key], val],
    }));
  };

  const toggleCheckboxButton = (key) => {
    userActionRef.current = true;
    setCustomCheckboxButtonStates((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Clear all ──────────────────────────────────────────────────────────────
  const clearAll = () => {
    userActionRef.current = true;
    setSearchQuery("");
    lastSyncedQueryRef.current = "";
    setSelectedStatuses([]);
    setSelectedModules([]);
    setSelectedUsers([]);
    setSelectedProjects([]);
    setSelectedCustomers([]);
    setSelectedContacts([]);
    setSelectedDepartments([]);
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSelectedTags([]);
    const clearedCcb = {};
    customCheckboxButtonFilters.forEach((f) => { clearedCcb[f.key] = false; });
    setCustomCheckboxButtonStates(clearedCcb);
    const clearedCd = {};
    customDropdownFilters.forEach((f) => { clearedCd[f.key] = []; });
    setCustomDropdownStates(clearedCd);
    const clearedCr = {};
    customRangeFilters.forEach((f) => { clearedCr[`${f.key}_min`] = ""; clearedCr[`${f.key}_max`] = ""; });
    setCustomRangeStates(clearedCr);
  };

  // ── Status options ─────────────────────────────────────────────────────────
  const allStatusOptions = Object.entries(status).map(([key, val]) => ({
    value: key,
    label: val.label,
    class: val.class,
  }));
  const statusOptions = (suppliedStatusOptions || (acceptedStatuses
    ? allStatusOptions.filter((o) => acceptedStatuses.includes(o.value))
    : allStatusOptions)).map((option) => ({ ...option, label: t(option.label) }));

  // ── hasActiveFilters ───────────────────────────────────────────────────────
  const hasActiveFilters =
    searchQuery ||
    selectedStatuses.length > 0 || selectedModules.length > 0 || selectedUsers.length > 0 ||
    selectedProjects.length > 0 || selectedCustomers.length > 0 || selectedContacts.length > 0 ||
    selectedDepartments.length > 0 || selectedCategories.length > 0 || selectedBrands.length > 0 ||
    selectedTags.length > 0 ||
    Object.values(customCheckboxButtonStates).some((v) => v === true) ||
    Object.values(customDropdownStates).some((v) => v.length > 0) ||
    Object.values(customRangeStates).some((v) => v !== "");

  // ── Active chips ───────────────────────────────────────────────────────────
  const chips = [];

  selectedStatuses.forEach((v) => {
    const opt = statusOptions.find((o) => o.value === v);
    if (opt) chips.push({ id: `status-${v}`, label: opt.label, colorClass: opt.class, onRemove: () => toggleStatus(v) });
  });
  selectedCustomers.forEach((v) => {
    const opt = customerOptions.find((o) => o.value === v || o.value === String(v));
    chips.push({ id: `cust-${v}`, label: opt?.label || v, colorClass: "bg-blue-50 text-blue-800", onRemove: () => toggleCustomer(v) });
  });
  selectedContacts.forEach((v) => {
    const opt = contactOptions.find((o) => o.value === v || o.value === String(v));
    chips.push({ id: `cont-${v}`, label: opt?.label || v, colorClass: "bg-purple-50 text-purple-800", onRemove: () => toggleContact(v) });
  });
  selectedDepartments.forEach((v) => {
    const opt = departmentOptions.find((o) => o.value === v || o.value === String(v));
    chips.push({ id: `dept-${v}`, label: opt?.label || v, colorClass: "bg-teal-50 text-teal-800", onRemove: () => toggleDepartment(v) });
  });
  selectedModules.forEach((v) => {
    const opt = moduleOptions.find((o) => o.value === v);
    chips.push({ id: `mod-${v}`, label: opt?.label || v, colorClass: "bg-slate-100 text-slate-700", onRemove: () => toggleModule(v) });
  });
  selectedUsers.forEach((v) => {
    const opt = userOptions.find((o) => o.value === v);
    chips.push({ id: `user-${v}`, label: opt?.label || v, colorClass: "bg-indigo-50 text-indigo-800", onRemove: () => toggleUser(v) });
  });
  selectedCategories.forEach((v) => {
    const opt = categoryOptions.find((o) => o.value === v);
    chips.push({ id: `cat-${v}`, label: opt?.label || v, colorClass: "bg-amber-50 text-amber-800", onRemove: () => toggleCategory(v) });
  });
  selectedBrands.forEach((v) => {
    const opt = brandOptions.find((o) => o.value === v);
    chips.push({ id: `brand-${v}`, label: opt?.label || v, colorClass: "bg-rose-50 text-rose-800", onRemove: () => toggleBrand(v) });
  });
  selectedTags.forEach((v) => {
    const opt = tagOptions.find((o) => o.value === v || o.value === String(v));
    const chipStyle = opt?.color
      ? { colorClass: "", style: { backgroundColor: `${opt.color}30`, color: opt.color } }
      : { colorClass: "bg-slate-100 text-slate-700", style: undefined };
    chips.push({
      id: `tag-${v}`,
      label: opt?.label || v,
      colorClass: chipStyle.colorClass,
      style: chipStyle.style,
      onRemove: () => toggleTag(v),
    });
  });
  customDropdownFilters.forEach((f) => {
    (customDropdownStates[f.key] || []).forEach((v) => {
      const opt = f.options?.find((o) => o.value === v || o.value === String(v));
      chips.push({ id: `cd-${f.key}-${v}`, label: opt?.label || v, colorClass: f.chipClass || "bg-slate-100 text-slate-700", onRemove: () => toggleCustomDropdown(f.key, v) });
    });
  });

  // ── Card wrapper class ─────────────────────────────────────────────────────
  const outerClass = filterCard
    ? "rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)] p-4 space-y-3.5"
    : "space-y-3.5";

  // ── Render ─────────────────────────────────────────────────────────────────
  if (chipsOnly) {
    if (chips.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <FilterChip
            key={chip.id}
            label={chip.label}
            colorClass={chip.colorClass}
            style={chip.style}
            onRemove={chip.onRemove}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={outerClass}>
      {/* Action bar */}
      {selectedItems && selectedItems.length > 0 && actionBar && (
        <div className="rounded-2xl border border-slate-200 bg-slate-900 px-5 py-3 text-sm text-white shadow-sm">
          {actionBar}
        </div>
      )}

      {/* Segmented control (e.g. project scope pills) */}
      {segmentedControl &&
        Array.isArray(segmentedControl.options) &&
        segmentedControl.options.length > 0 && (
          <div
            role={panelMode ? "tablist" : undefined}
            className={TWcn(
              panelMode
                ? "grid w-full gap-0.5 rounded-xl bg-slate-100 p-1"
                : "flex flex-wrap gap-2"
            )}
            style={
              panelMode
                ? {
                    gridTemplateColumns: `repeat(${segmentedControl.options.length}, minmax(0, 1fr))`,
                  }
                : undefined
            }
          >
            {segmentedControl.options.map((opt) => {
              const active = segmentedControl.value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role={panelMode ? "tab" : undefined}
                  aria-selected={panelMode ? active : undefined}
                  disabled={disabled}
                  onClick={() => segmentedControl.onChange?.(opt.value)}
                  className={TWcn(
                    "inline-flex items-center justify-center font-semibold transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                    panelMode
                      ? TWcn(
                          "min-h-11 touch-manipulation select-none rounded-[10px] px-2 text-xs",
                          active
                            ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80"
                            : "text-slate-600 active:bg-white/60"
                        )
                      : TWcn(
                          "rounded-full border",
                          "h-[46px] px-[18px] text-[14.5px]",
                          active
                            ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
                            : "border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50"
                        )
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

      {/* ── Row 1: Search + filter pills ────────────────────────── */}
      {panelMode ? (
        <div className="space-y-3">
          {!hideSearch && (
          <div
            className={TWcn(
              "relative flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 transition-[border-color,box-shadow]",
              "h-[42px] focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200"
            )}
          >
            <MagnifyingGlassIcon className="h-[18px] w-[18px] flex-shrink-0 text-slate-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => {
                userActionRef.current = true;
                setSearchQuery(e.target.value);
                lastSyncedQueryRef.current = e.target.value;
              }}
              onFocus={() => { isSearchFocusedRef.current = true; }}
              onBlur={() => {
                isSearchFocusedRef.current = false;
                setTimeout(() => { userActionRef.current = false; }, 200);
              }}
              placeholder={t(searchPlaceholder)}
              className="flex-1 border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  userActionRef.current = true;
                  setSearchQuery("");
                  lastSyncedQueryRef.current = "";
                }}
                className="flex-shrink-0 text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="h-[15px] w-[15px]" />
              </button>
            )}
          </div>
          )}

          {moduleOptions.length > 0 && (
            <FilterPill
              label={t("Module")}
              icon={TagIcon}
              count={selectedModules.length}
              active={selectedModules.length > 0}
              disabled={disabled}
              fullWidth
              onClick={(e) => openPopover("modules", e)}
            />
          )}

          {customerOptions.length > 0 && (
            <FilterPill
              label={t("Customer")}
              icon={BuildingOffice2Icon}
              count={selectedCustomers.length}
              active={selectedCustomers.length > 0}
              disabled={disabled}
              fullWidth
              onClick={(e) => openPopover("customers", e)}
            />
          )}

          {contactOptions.length > 0 && (
            <FilterPill
              label={t("Contact")}
              icon={PhoneIcon}
              count={selectedContacts.length}
              active={selectedContacts.length > 0}
              disabled={disabled}
              fullWidth
              onClick={(e) => openPopover("contacts", e)}
            />
          )}

          {departmentOptions.length > 0 && (
            <FilterPill
              label={t("Department")}
              icon={Squares2X2Icon}
              count={selectedDepartments.length}
              active={selectedDepartments.length > 0}
              disabled={disabled}
              fullWidth
              onClick={(e) => openPopover("departments", e)}
            />
          )}

          {userOptions.length > 0 && (
            <FilterPill
              label={usersFilterLabel || t("Users")}
              icon={UserGroupIcon}
              count={selectedUsers.length}
              active={selectedUsers.length > 0}
              disabled={disabled}
              fullWidth
              onClick={(e) => openPopover("users", e)}
            />
          )}

          {tagOptions.length > 0 && (
            <FilterPill
              label={tagsFilterLabel || t("Labels")}
              icon={TagIcon}
              count={selectedTags.length}
              active={selectedTags.length > 0}
              disabled={disabled}
              fullWidth
              onClick={(e) => openPopover("tags", e)}
            />
          )}

          {!hideStatusFilter && statusOptions.length > 0 && (
            <FilterPill
              label={t("Status")}
              icon={FunnelIcon}
              count={selectedStatuses.length}
              active={selectedStatuses.length > 0}
              disabled={disabled}
              fullWidth
              onClick={(e) => openPopover("status", e)}
            />
          )}

          {customDropdownFilters.map((f) => {
            const count = (customDropdownStates[f.key] || []).length;
            return (
              <FilterPill
                key={f.key}
                label={t(f.label)}
                icon={f.icon}
                count={count}
                active={count > 0}
                disabled={disabled}
                fullWidth
                onClick={(e) => openPopover(`cd_${f.key}`, e)}
              />
            );
          })}

          {panelPlaceholderSlots.map((slot) => (
            <FilterPill
              key={slot.key || slot.label}
              label={slot.label}
              icon={slot.icon}
              count={0}
              active={false}
              disabled
              fullWidth
            />
          ))}

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex h-[42px] w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none"
            >
              <XMarkIcon className="h-[15px] w-[15px]" />
              {t("Clear")}
            </button>
          )}
        </div>
      ) : (
      <div className="flex flex-wrap items-center gap-2.5">
        {!hideSearch && compactSearch && !searchExpanded && !searchQuery ? (
          <button
            type="button"
            aria-label={t(searchPlaceholder)}
            onClick={() => {
              setSearchExpanded(true);
              setTimeout(() => searchInputRef.current?.focus(), 0);
            }}
            className={TWcn(
              "inline-flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none",
              filterCard ? "h-[46px] w-[46px]" : "h-[40px] w-[40px]"
            )}
          >
            <MagnifyingGlassIcon className="h-[18px] w-[18px]" />
          </button>
        ) : !hideSearch ? (
        /* Search pill */
        <div
          className={TWcn(
            "relative flex items-center gap-3 rounded-full border border-slate-200 bg-white px-5 transition-[border-color,box-shadow]",
            compactSearch ? "min-w-[180px] flex-1" : "min-w-[220px] flex-1",
            filterCard ? "h-[46px]" : "h-[40px]",
            "focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200"
          )}
        >
          <MagnifyingGlassIcon className="h-[18px] w-[18px] flex-shrink-0 text-slate-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              userActionRef.current = true;
              setSearchQuery(e.target.value);
              lastSyncedQueryRef.current = e.target.value;
            }}
            onFocus={() => { isSearchFocusedRef.current = true; }}
            onBlur={() => {
              isSearchFocusedRef.current = false;
              if (compactSearch && !searchQuery) {
                setSearchExpanded(false);
              }
              setTimeout(() => { userActionRef.current = false; }, 200);
            }}
            placeholder={t(searchPlaceholder)}
            className="flex-1 border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                userActionRef.current = true;
                setSearchQuery("");
                lastSyncedQueryRef.current = "";
                if (compactSearch) setSearchExpanded(false);
              }}
              className="flex-shrink-0 text-slate-400 hover:text-slate-600"
            >
              <XMarkIcon className="h-[15px] w-[15px]" />
            </button>
          )}
        </div>
        ) : null}

        {/* Status filter pill */}
        {!hideStatusFilter && statusOptions.length > 0 && (
          <FilterPill
            label={t("Status")}
            icon={FunnelIcon}
            count={selectedStatuses.length}
            active={selectedStatuses.length > 0}
            disabled={disabled}
            onClick={(e) => openPopover("status", e)}
          />
        )}

        {/* Customer filter pill */}
        {customerOptions.length > 0 && (
          <FilterPill
            label={t("Customer")}
            icon={BuildingOffice2Icon}
            count={selectedCustomers.length}
            active={selectedCustomers.length > 0}
            disabled={disabled}
            onClick={(e) => openPopover("customers", e)}
          />
        )}

        {/* Contact filter pill */}
        {contactOptions.length > 0 && (
          <FilterPill
            label={t("Contact")}
            icon={PhoneIcon}
            count={selectedContacts.length}
            active={selectedContacts.length > 0}
            disabled={disabled}
            onClick={(e) => openPopover("contacts", e)}
          />
        )}

        {/* Department filter pill */}
        {departmentOptions.length > 0 && (
          <FilterPill
            label={t("Department")}
            icon={Squares2X2Icon}
            count={selectedDepartments.length}
            active={selectedDepartments.length > 0}
            disabled={disabled}
            onClick={(e) => openPopover("departments", e)}
          />
        )}

        {/* Module filter pill */}
        {moduleOptions.length > 0 && (
          <FilterPill
            label={t("Module")}
            icon={TagIcon}
            count={selectedModules.length}
            active={selectedModules.length > 0}
            disabled={disabled}
            onClick={(e) => openPopover("modules", e)}
          />
        )}

        {/* User filter pill */}
        {userOptions.length > 0 && (
          <FilterPill
            label={usersFilterLabel || t("Users")}
            icon={UserGroupIcon}
            count={selectedUsers.length}
            active={selectedUsers.length > 0}
            disabled={disabled}
            onClick={(e) => openPopover("users", e)}
          />
        )}

        {/* Tag / label filter pill */}
        {tagOptions.length > 0 && (
          <FilterPill
            label={tagsFilterLabel || t("Labels")}
            icon={TagIcon}
            count={selectedTags.length}
            active={selectedTags.length > 0}
            disabled={disabled}
            onClick={(e) => openPopover("tags", e)}
          />
        )}

        {/* Category filter pill */}
        {(categoryOptions.length > 0 || categoryTree.length > 0) && (
          <FilterPill
            label={t("Category")}
            icon={TagIcon}
            count={selectedCategories.length}
            active={selectedCategories.length > 0}
            disabled={disabled}
            onClick={(e) => openPopover("categories", e)}
          />
        )}

        {/* Brand filter pill */}
        {brandOptions.length > 0 && (
          <FilterPill
            label={t("Brand")}
            count={selectedBrands.length}
            active={selectedBrands.length > 0}
            disabled={disabled}
            onClick={(e) => openPopover("brands", e)}
          />
        )}

        {/* Custom dropdown filter pills */}
        {customDropdownFilters.map((f) => {
          const count = (customDropdownStates[f.key] || []).length;
          return (
            <FilterPill
              key={f.key}
              label={t(f.label)}
              icon={f.icon}
              count={count}
              active={count > 0}
              disabled={disabled}
              onClick={(e) => openPopover(`cd_${f.key}`, e)}
            />
          );
        })}

        {/* Custom toggle pills */}
        {customCheckboxButtonFilters.map((f) => (
          <TogglePill
            key={f.key}
            label={t(f.label)}
            icon={f.icon}
            active={!!customCheckboxButtonStates[f.key]}
            disabled={disabled}
            onClick={() => toggleCheckboxButton(f.key)}
          />
        ))}

        {/* Clear all — ghost pill */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex h-[46px] items-center gap-2 rounded-full px-[18px] text-[14.5px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus:outline-none"
          >
            <XMarkIcon className="h-[15px] w-[15px]" />
            {t("Clear")}
          </button>
        )}
      </div>
      )}

      {/* ── Row 2: Active filter chips ───────────────────────────── */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <FilterChip
              key={chip.id}
              label={chip.label}
              colorClass={chip.colorClass}
              style={chip.style}
              onRemove={chip.onRemove}
            />
          ))}
        </div>
      )}

      {/* ── Popovers ─────────────────────────────────────────────── */}

      {popover.field === "status" && (
        <PMSelectMenu
          {...selectMenuZIndex}
          anchorRect={popover.rect}
          onClose={closePopover}
          multi
          searchable={statusOptions.length > 8}
          options={statusOptions.map((o) => ({ id: o.value, label: o.label }))}
          value={selectedStatuses}
          onPick={toggleStatus}
          label={t("Status")}
        />
      )}

      {popover.field === "customers" && (
        <PMSelectMenu
          {...selectMenuZIndex}
          anchorRect={popover.rect}
          onClose={closePopover}
          multi
          searchable
          options={customerOptions.map((o) => ({ id: o.value, label: o.label }))}
          value={selectedCustomers}
          onPick={toggleCustomer}
          label={t("Customer")}
        />
      )}

      {popover.field === "contacts" && (
        <PMSelectMenu
          {...selectMenuZIndex}
          anchorRect={popover.rect}
          onClose={closePopover}
          multi
          searchable
          options={contactOptions.map((o) => ({ id: o.value, label: o.label }))}
          value={selectedContacts}
          onPick={toggleContact}
          label={t("Contact")}
        />
      )}

      {popover.field === "departments" && (
        <PMSelectMenu
          {...selectMenuZIndex}
          anchorRect={popover.rect}
          onClose={closePopover}
          multi
          searchable={departmentOptions.length > 8}
          options={departmentOptions.map((o) => ({ id: o.value, label: o.label }))}
          value={selectedDepartments}
          onPick={toggleDepartment}
          label={t("Department")}
        />
      )}

      {popover.field === "modules" && (
        <PMSelectMenu
          {...selectMenuZIndex}
          anchorRect={popover.rect}
          onClose={closePopover}
          multi
          searchable={moduleOptions.length > 8}
          options={moduleOptions.map((o) => ({ id: o.value, label: o.label }))}
          value={selectedModules}
          onPick={toggleModule}
          label={t("Module")}
        />
      )}

      {popover.field === "users" && (
        <PMSelectMenu
          {...selectMenuZIndex}
          anchorRect={popover.rect}
          onClose={closePopover}
          multi
          searchable
          options={userOptions.map((o) => ({ id: o.value, label: o.label }))}
          value={selectedUsers}
          onPick={toggleUser}
          label={usersFilterLabel || t("Users")}
        />
      )}

      {popover.field === "tags" && (
        <PMSelectMenu
          {...selectMenuZIndex}
          anchorRect={popover.rect}
          onClose={closePopover}
          multi
          searchable
          options={tagOptions.map((o) => ({
            id: o.value,
            label: o.label,
            color: o.color,
          }))}
          value={selectedTags}
          onPick={toggleTag}
          label={tagsFilterLabel || t("Labels")}
          renderOption={(o) => (
            <span className="flex min-w-0 items-center gap-2">
              {o.color ? (
                <span
                  className="h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: o.color }}
                />
              ) : null}
              <span className="truncate">{o.label}</span>
            </span>
          )}
        />
      )}

      {popover.field === "categories" && (
        <PMSelectMenu
          {...selectMenuZIndex}
          anchorRect={popover.rect}
          onClose={closePopover}
          multi
          searchable
          options={
            categoryOptions.length > 0
              ? categoryOptions.map((o) => ({ id: o.value, label: o.label }))
              : categoryTree.map((o) => ({ id: o.value || o.id, label: o.title || o.name || o.displayTitle || o.label || "" }))
          }
          value={selectedCategories}
          onPick={toggleCategory}
          label={t("Category")}
        />
      )}

      {popover.field === "brands" && (
        <PMSelectMenu
          {...selectMenuZIndex}
          anchorRect={popover.rect}
          onClose={closePopover}
          multi
          searchable={brandOptions.length > 8}
          options={brandOptions.map((o) => ({ id: o.value, label: o.label }))}
          value={selectedBrands}
          onPick={toggleBrand}
          label={t("Brand")}
        />
      )}

      {/* Custom dropdown popovers */}
      {customDropdownFilters.map((f) =>
        popover.field === `cd_${f.key}` ? (
          <PMSelectMenu
            key={f.key}
            {...selectMenuZIndex}
            anchorRect={popover.rect}
            onClose={closePopover}
            multi
            searchable={f.options?.length > 8}
            options={(f.options || []).map((o) => ({ id: o.value, label: o.label }))}
            value={customDropdownStates[f.key] || []}
            onPick={(val) => toggleCustomDropdown(f.key, val)}
            label={t(f.label)}
          />
        ) : null
      )}
    </div>
  );
};
